import _ from 'lodash';
import React from 'react';

import expansionOpts from 'shared/expansionOptions';

export const simcVersions = [{
  value: 'release',
  name: 'Release',
  description: 'Latest official release',
  public: false,
}, {
  value: 'weekly',
  name: 'Weekly',
  description: 'Built from source every Monday',
  default: true,
  public: true,
}, {
  value: 'nightly',
  name: 'Nightly',
  description: 'Nightly build from source, most up-to-date but low chance of bugs',
  public: true,
}, {
  value: 'shadowlands',
  name: 'Shadowlands',
  description: 'Nightly shadowlands build',
  public: false,
}];

export const SMART_SIM_VALUE = 'smart';
export const SMART_SIM_ITERATIONS = 5000;

export const isSmartIterations = iterations => iterations === SMART_SIM_VALUE;

export const getIterations = (iterations) => {
  return isSmartIterations(iterations) ? SMART_SIM_ITERATIONS : iterations;
};

const iterationOptionsInternal = [{
  value: 'smart',
  name: 'Smart Sim',
  description: (
    <span>
      Recommended - Raidbots chooses iterations and target error for you. Generally should not be changed.
      {' '}
      <a
        href="https://medium.com/raidbots/smart-sim-2b54952aba44"
        target="_blank"
        rel="noopener noreferrer"
      >
        More info
      </a>
    </span>
  ),
  public: true,
  default: true,
}, {
  value: 5000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 10000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 15000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 20000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 25000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 30000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 40000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 50000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 60000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 70000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 80000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 90000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 100000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}];

if (process.env.NODE_ENV === 'development') {
  iterationOptionsInternal.unshift({
    value: 2499,
    public: true,
    description: 'DEV ONLY',
  });
  iterationOptionsInternal.unshift({
    value: 999,
    public: true,
    description: 'DEV ONLY',
  });
  iterationOptionsInternal.unshift({
    value: 99,
    public: true,
    description: 'DEV ONLY',
  });
}

export const iterationOptions = iterationOptionsInternal;

export const fightStyles = [{
  value: 'Patchwerk',
  name: 'Patchwerk',
  description: 'No movement, single target',
  public: true,
  default: true,
}, {
  value: 'DungeonSlice',
  name: 'Dungeon Slice (BETA)',
  description: (
    <span>
      BETA! 6 minute slice of an M+ dungeon.  Overrides Fight Length, uses buffs from scrolls
      Disables raid specific buffs.
      {' '}
      <a
        href="https://medium.com/raidbots/mythic-simming-with-dungeon-slice-68a834880975"
        target="_blank"
        rel="noopener noreferrer"
      >
        More info
      </a>
    </span>
  ),
  public: true,
  overrides: {
    enemyCount: 1,
    fightLength: 360,
    reoriginationArray: 0,
    bloodlust: 1,
    arcaneIntellect: 1,
    fortitude: 1,
    battleShout: 1,
    windfury: 0,
    nazjatar: false,
    nyalotha: false,
  },
}, {
  value: 'TargetDummy',
  name: 'Target Dummy',
  description: 'Always 100% health, raid buffs/debuffs disabled, no consumables',
  public: true,
  overrides: {
    fightStyle: null,
    reoriginationArray: 0,
    bloodlust: 0,
    arcaneIntellect: 0,
    fortitude: 0,
    battleShout: 0,
    mysticTouch: 0,
    chaosBrand: 0,
    windfury: 0,
    bleeding: 0,
    potion: 'disabled',
    food: 'disabled',
    flask: 'disabled',
    augmentation: 'disabled',
    zuldazar: 0,
    nazjatar: false,
    nyalotha: false,
    covenantChance: 100,
    worldveinAllies: 0,
    loyalToTheEndAllies: 0,
    surgingVitality: 0,
    temporaryEnchant: 'disabled',
    enemies: [{
      name: 'Target Dummy',
      properties: {
        enemy_fixed_health_percentage: 100,
      },
    }],
  },
  disableAdvanced: true,
}, {
  value: 'ExecutePatchwerk',
  name: 'Execute Patchwerk',
  description: 'Target starts at 20% HP',
  public: true,
  overrides: {
    fightStyle: null,
    enemies: [{
      name: 'Execute Range',
      properties: {
        enemy_initial_health_percentage: 20,
      },
    }],
  },
  disableAdvanced: true,
}, {
  value: 'HecticAddCleave',
  name: 'Hectic Add Cleave',
  description: 'Regular add spawns (5 adds up about 40% of the fight), frequent movement. Similar to T15 Horridon',
  public: true,
}, {
  value: 'LightMovement',
  name: 'Light Movement',
  description: 'Infrequent movement - Move 50 yards every 85 seconds',
  public: true,
}, {
  value: 'HeavyMovement',
  name: 'Heavy Movement',
  description: 'Frequent movement - Move 25 yards every 10 seconds',
  public: true,
}, {
  value: 'CastingPatchwerk',
  name: 'Casting Patchwerk',
  description: 'Patchwerk that is always casting, can be useful for simming interrupts',
  public: true,
}, {
  value: 'CleaveAdd',
  name: 'Cleave Add',
  description: 'Single add is up about 60% of the fight',
  public: true,
}, {
  value: 'HelterSkelter',
  name: 'Helter Skelter',
  description: 'Movement every 30 seconds, interrupts every 30 seconds, boss invulnerable every 120 seconds',
  public: true,
}, {
  value: 'Beastlord',
  name: 'Beastlord',
  description: 'Fight with a lot of various add packs up most of the fight. Similar to T17 Beastlord Darmac',
  public: true,
}, {
  value: 'Ultraxion',
  name: 'Ultraxion',
  description: 'Single target fight with player stuns and damage to the raid',
  public: true,
}];
export const fightStylesLower = _.mapValues(fightStyles, style => ({
  ...style,
  value: _.toLower(style.value),
}));


export const fightLengths = [{
  value: 20,
  name: '20 seconds',
  public: true,
}, {
  value: 40,
  name: '40 seconds',
  public: true,
}, {
  value: 60,
  name: '1 minute',
  public: true,
}, {
  value: 90,
  name: '1 minute, 30 seconds',
  public: true,
}, {
  value: 120,
  name: '2 minutes',
  public: true,
}, {
  value: 180,
  name: '3 minutes',
  public: true,
}, {
  value: 240,
  name: '4 minutes',
  public: true,
}, {
  value: 300,
  name: '5 minutes',
  public: true,
  default: true,
}, {
  value: 360,
  name: '6 minutes',
  public: true,
}, {
  value: 420,
  name: '7 minutes',
  public: true,
}, {
  value: 480,
  name: '8 minutes',
  public: true,
}, {
  value: 540,
  name: '9 minutes',
  public: true,
}, {
  value: 600,
  name: '10 minutes',
  public: true,
}];

export const enemyCounts = [{
  value: 1,
  name: '1 Boss',
  description: 'Default and best for single target sims',
  default: true,
}, {
  value: 2,
  name: '2 Bosses',
  description: 'Two stacked bosses up at all times, good for multitarget sims',
}, {
  value: 3,
  name: '3 Bosses',
  description: 'Three stacked bosses up at all times, good for multitarget sims',
}, {
  value: 4,
  name: '4 Bosses',
  description: '',
}, {
  value: 5,
  name: '5 Bosses',
  description: '',
}, {
  value: 6,
  name: '6 Bosses',
  description: '',
}, {
  value: 7,
  name: '7 Bosses',
  description: '',
}, {
  value: 8,
  name: '8 Bosses',
  description: '',
}, {
  value: 9,
  name: '9 Bosses',
  description: '',
}, {
  value: 10,
  name: '10 Bosses',
  description: '',
}];

export const enemyTypes = [{
  value: 'FluffyPillow',
  name: 'Fluffy Pillow',
  description: 'Default boss type, best for most DPS simulations',
  default: true,
  public: true,
}];


export const pantheonTrinkets = _.map(_.range(0, 31), num => ({
  value: num,
  name: num,
  description: `${num} other raid member${num !== 1 ? 's' : ''} with trinket`,
  default: num === 0,
}));

export const reoriginationArrayStacks = _.union(
  [{
    value: -1,
    name: 'Smart',
    description: 'Use the value provided from the SimC addon or from Raidbots cache',
    default: false,
  }, {
    value: 0,
    name: 0,
    description: 'Do not sim Reorigination Array (not in Uldir or no boss kills)',
    default: true,
  }],
  _.map(_.range(1, 11), num => ({
    value: num,
    name: num,
    description: `${num} stacks will provide ${num * 75} of your highest stat`,
    default: false,
  })),
);

export const foods = [{
  value: '',
  name: 'SimC Default',
  default: true,
}, {

  // Shadowlands Food

  value: 'feast_of_gluttonous_hedonism',
  shortName: 'Feast of Gluttonous Hedonism',
  name: 'Feast of Gluttonous Hedonism (20 Main Stat)',
  itemId: 172043,
  icon: 'inv_tradeskill_cooking_shadowlandsfeast_large01',
}, {
  value: 'surprisingly_palatable_feast',
  shortName: 'Surprisingly Palatable Feast',
  name: 'Surprisingly Palatable Feast (18 Main Stat)',
  itemId: 172042,
  icon: 'inv_tradeskill_cooking_shadowlandsfeast_small01',
}, {

  value: 'smothered_shank',
  shortName: 'Smothered Shank',
  name: 'Smothered Shank (Fire Breath)',
  itemId: 172062,
  icon: 'inv_cooking_90_smotheredshank',
}, {

  value: 'spinefin_souffle_and_fries',
  shortName: 'Spinefin Souffle and Fries',
  name: 'Spinefin Souffle and Fries (30 Crit)',
  itemId: 172041,
  icon: 'inv_cooking_90_phantasmalsoufflefries',
}, {
  value: 'tenebrous_crown_roast_aspic',
  shortName: 'Tenebrous Crown Roast Aspic',
  name: 'Tenebrous Crown Roast Aspic (30 Haste)',
  itemId: 172045,
  icon: 'inv_cooking_90_tenebrouscrownroastaspic',
}, {
  value: 'iridescent_ravioli_with_apple_sauce',
  shortName: 'Iridescent Ravioli with Apple Sauce',
  name: 'Iridescent Ravioli with Apple Sauce (30 Mastery)',
  itemId: 172049,
  icon: 'inv_cooking_90_crawlerravioliapplesauce',
}, {
  value: 'steak_a_la_mode',
  shortName: 'Steak a la Mode',
  name: 'Steak a la Mode (30 Vers)',
  itemId: 172051,
  icon: 'inv_cooking_90_steakalamode',
}, {
  value: 'banana_beef_pudding',
  shortName: 'Banana Beef Pudding',
  name: 'Banana Beef Pudding (22 Stam)',
  itemId: 172069,
  icon: 'inv_cooking_90_bananabeefpudding',
}, {

  value: 'butterscotch_marinated_ribs',
  shortName: 'Butterscotch Marinated Ribs',
  name: 'Butterscotch Marinated Ribs (18 Crit)',
  itemId: 172040,
  icon: 'inv_cooking_90_butterscotchmarinatedribs',
}, {
  value: 'cinnamon_bonefish_stew',
  shortName: 'Cinnamon Bonefish Stew',
  name: 'Cinnamon Bonefish Stew (18 Haste)',
  itemId: 172044,
  icon: 'inv_cooking_90_cinnamonbonefishstew',
}, {
  value: 'meaty_apple_dumplings',
  shortName: 'Meaty Apple Dumplings',
  name: 'Meaty Apple Dumplings (18 Mastery)',
  itemId: 172048,
  icon: 'inv_cooking_90_meatyappledumplings',
}, {
  value: 'sweet_silvergill_sausages',
  shortName: 'Sweet Silvergill Sausages',
  name: 'Sweet Silvergill Sausages (18 Vers)',
  itemId: 172050,
  icon: 'inv_cooking_90_silvergillsausage',
}, {
  value: 'pickled_meat_smoothie',
  shortName: 'Pickled Meat Smoothie',
  name: 'Pickled Meat Smoothie (14 Stam)',
  itemId: 172068,
  icon: 'inv_cooking_90_pickledmeatsmoothie',
}, {

  // 8.2 food

  value: 'famine_evaluator_and_snack_table',
  shortName: 'F.E.A.S.T.',
  name: 'BfA - F.E.A.S.T. (Main Stat)',
  itemId: 168315,
  icon: 'trade_archaeology_staffofsorcerer_than-thaurissan',

}, {
  value: 'fragrant_kakavia',
  shortName: 'Fragrant Kakavia',
  name: 'BfA - Fragrant Kakavia (Stamina)',
  itemId: 168312,
  icon: 'inv_misc_food_159_fish_white',
}, {
  value: 'mechdowels_big_mech',
  shortName: 'Big Mech',
  name: 'BfA - Mech Dowel\'s "Big Mech" (Crit)',
  itemId: 168310,
  icon: 'inv_misc_starflowersandwich',
}, {
  value: 'abyssalfried_rissole',
  shortName: 'Abyssal-Fried Rissole',
  name: 'BfA - Abyssal-Fried Rissole (Mastery)',
  itemId: 168311,
  icon: 'inv_misc_fish_18',
}, {
  value: 'baked_port_tato',
  shortName: 'Baked Port Tato',
  name: 'BfA - Baked Port Tato (Haste)',
  itemId: 168313,
  icon: 'inv_misc_food_cooked_fishcake',
}, {
  value: 'biltong',
  shortName: 'Bil\'Tong',
  name: 'BfA - Bil\'Tong (Vers)',
  itemId: 168314,
  icon: 'inv_misc_food_48',

  /*
}, {

  // 8.0 food

  value: 'bountiful_captains_feast',
  shortName: 'Captain\'s Feast',
  name: 'BfA - Bountiful Captain\'s Feast (Main Stat)',
  itemId: 156526,
  icon: 'inv_cooking_80_majorfeast',
}, {
  value: 'boralus_blood_sausage',
  shortName: 'Boralus Blood Sausage',
  name: 'BfA - Boralus Blood Sausage (Main Stat)',
  itemId: 156526,
  icon: 'inv_cooking_80_majorfeast',
}, {
  value: 'galley_banquet',
  shortName: 'Galley Banquet',
  name: 'BfA - Gallery Banquet (Main Stat)',
  itemId: 156525,
  icon: 'inv_cooking_80_minorfeast',
}, {
  value: 'honeyglazed_haunches',
  shortName: 'Honey-Glazed Haunches',
  name: 'BfA - Honey-Glazed Haunches (Crit)',
  itemId: 154884,
  icon: 'inv_cooking_80_honeyglazedhaunch',
}, {
  value: 'swamp_fish_n_chips',
  shortName: 'Swamp Fish \'n Chips',
  name: 'BfA - Swamp Fish \'n Chips (Haste)',
  itemId: 154884,
  icon: 'inv_cooking_80_swampfishnchips',
}, {
  value: 'sailors_pie',
  shortName: 'Sailor\'s Pie',
  name: 'BfA - Sailor\'s Pie (Mastery)',
  itemId: 154888,
  icon: 'inv_cooking_80_sailorspie',
}, {
  value: 'spiced_snapper',
  shortName: 'Spiced Snapper',
  name: 'BfA - Spiced Snapper (Vers)',
  itemId: 154886,
  icon: 'inv_cooking_80_spicedcatfish',
}, {
  value: 'kul_tiramisu',
  shortName: 'Kul Tiramisu',
  name: 'BfA - Kul Tiramisu (Crit)',
  itemId: 154881,
  icon: 'inv_cooking_80_kultiramisu',
}, {
  value: 'ravenberry_tarts',
  shortName: 'Ravenberry Tarts',
  name: 'BfA - Ravenberry Tarts (Haste)',
  itemId: 154884,
  icon: 'inv_cooking_80_ravenberrytart',
}, {
  value: 'loa_loaf',
  shortName: 'Loa Loaf',
  name: 'BfA - Loa Loaf (Mastery)',
  itemId: 154887,
  icon: 'inv_cooking_80_loaloaf',
}, {
  value: 'mondazi',
  shortName: 'Mon\'Dazi',
  name: 'BfA - Mon\'Dazi (Vers)',
  itemId: 154885,
  icon: 'inv_cooking_80_mondazi',
}, {
  value: 'lavish_suramar_feast',
  shortName: 'Lavish Suramar Feast',
  name: 'Legion - Lavish Suramar Feast (Main Stat)',
}, {
  value: 'azshari_salad',
  shortName: 'Azshari Salad',
  name: 'Legion - Azshari Salad (Haste)',
}, {
  value: 'nightborne_delicacy_platter',
  shortName: 'Nightborne Delicacy Platter',
  name: 'Legion - Nightborne Delicacy Platter (Mastery)',
}, {
  value: 'the_hungry_magister',
  shortName: 'Hungry Magister',
  name: 'Legion - The Hungry Magister (Crit)',
}, {
  value: 'seedbattered_fish_plate',
  shortName: 'Seed-Battered Fish Plate',
  name: 'Legion - Seed-Battered Fish Plate (Vers)',
}, {
  value: 'lemon_herb_filet',
  shortName: 'Lemon Herb Filet',
  name: 'WoD - Lemon Herb Filet (Vers)',
  */
}, {
  value: 'disabled',
  name: 'No Food',
  shortName: 'No Food',
}];

export const potions = [{
  value: '',
  name: 'SimC Default',
  default: true,
}, {

  // Shadowlands Potions

  value: 'potion_of_deathly_fixation',
  name: 'Potion of Deathly Fixation',
  shortName: 'Deathly Fixation',
  itemId: 171351,
  icon: 'inv_alchemy_90_combat1_yellow',
  /*
}, {
  value: 'potion_of_divine_awakening',
  name: 'Potion of Divine Awakening',
  shortName: 'Divine Awakening',
  itemId: 171350,
  icon: 'inv_alchemy_90_combat1_orange',
  */
}, {
  value: 'potion_of_empowered_exorcisms',
  name: 'Potion of Empowered Exorcisms',
  shortName: 'Empowered Exorcisms',
  itemId: 171352,
  icon: 'inv_alchemy_90_combat1_pink',
}, {
  value: 'potion_of_hardened_shadows',
  name: 'Potion of Hardened Shadows',
  shortName: 'Hardened Shadows',
  itemId: 171271,
  icon: 'inv_alchemy_90_combat2_blue',
}, {
  value: 'potion_of_phantom_fire',
  name: 'Potion of Phantom Fire',
  shortName: 'Phantom Fire',
  itemId: 171349,
  icon: 'inv_alchemy_90_combat1_green',

  /*
}, {
  value: 'potion_of_sacrificial_anima',
  name: 'Potion of Sacrificial Anima',
  shortName: 'Sacrificial Anima',
  itemId: 176811,
  icon: 'inv_alchemy_90_combat1_red',
  */

}, {
  value: 'potion_of_spectral_agility',
  name: 'Potion of Spectral Agility',
  shortName: 'Spectral Agility',
  itemId: 171270,
  icon: 'inv_alchemy_90_combat2_green',
}, {
  value: 'potion_of_spectral_intellect',
  name: 'Potion of Spectral Intellect',
  shortName: 'Spectral Intellect',
  itemId: 171273,
  icon: 'inv_alchemy_90_combat2_purple',
}, {
  value: 'potion_of_spectral_strength',
  name: 'Potion of Spectral Strength',
  shortName: 'Spectral Strength',
  itemId: 171275,
  icon: 'inv_alchemy_90_combat2_yellow',
}, {
  value: 'potion_of_spectral_stamina',
  name: 'Potion of Spectral Stamina',
  shortName: 'Spectral Stamina',
  itemId: 171274,
  icon: 'inv_alchemy_90_combat2_red',
}, {


  // 8.2 Potions

  value: 'superior_battle_potion_of_agility',
  name: 'BfA - Superior Battle Potion of Agility',
  shortName: 'Superior Agility',
  itemId: 168489,
  icon: 'trade_alchemy_potione3',
}, {
  value: 'superior_battle_potion_of_intellect',
  name: 'BfA - Superior Battle Potion of Intellect',
  shortName: 'Superior Intellect',
  itemId: 168498,
  icon: 'trade_alchemy_potione2',
}, {
  value: 'superior_battle_potion_of_strength',
  name: 'BfA - Superior Battle Potion of Strength',
  shortName: 'Superior Strength',
  itemId: 168500,
  icon: 'trade_alchemy_potione1',
}, {
  value: 'superior_battle_potion_of_stamina',
  name: 'BfA - Superior Battle Potion of Stamina',
  shortName: 'Superior Stamina',
  itemId: 168499,
  icon: 'trade_alchemy_potione5',
}, {
  value: 'potion_of_focused_resolve',
  name: 'BfA - Potion of Focused Resolve',
  shortName: 'Focused Resolve',
  itemId: 168506,
  icon: 'inv_potionf_1',
}, {
  value: 'potion_of_empowered_proximity',
  name: 'BfA - Potion of Empowered Proximity',
  shortName: 'Empowered Proximity',
  itemId: 168529,
  icon: 'inv_potionf_2',
}, {
  value: 'potion_of_unbridled_fury',
  name: 'BfA - Potion of Unbridled Fury',
  shortName: 'Unbridled Fury',
  itemId: 169299,
  icon: 'inv_potionf_5',

  // 8.0 Potions

  /*
}, {

  value: 'battle_potion_of_agility',
  name: 'BfA - Battle Potion of Agility',
  shortName: 'Agility',
  itemId: 163223,
  icon: 'trade_alchemy_potionc6',
}, {
  value: 'battle_potion_of_intellect',
  name: 'BfA - Battle Potion of Intellect',
  shortName: 'Intellect',
  itemId: 163222,
  icon: 'trade_alchemy_potionc4',
}, {
  value: 'battle_potion_of_strength',
  name: 'BfA - Battle Potion of Strength',
  shortName: 'Strength',
  itemId: 163224,
  icon: 'trade_alchemy_potionc2',
}, {
  value: 'battle_potion_of_stamina',
  name: 'BfA - Battle Potion of Stamina',
  shortName: 'Stamina',
  itemId: 163225,
  icon: 'trade_alchemy_potionc3',
}, {
  value: 'potion_of_bursting_blood',
  name: 'BfA - Potion of Bursting Blood',
  shortName: 'Bursting Blood',
  itemId: 152560,
  icon: 'inv_alchemy_80_elixir01purple',
}, {
  value: 'potion_of_rising_death',
  name: 'BfA - Potion of Rising Death',
  shortName: 'Rising Death',
  itemId: 152559,
  icon: 'inv_alchemy_80_elixir01blue',
}, {

  // legacy that some people maybe want to test?

  value: 'prolonged_power',
  name: 'Legion - Potion of Prolonged Power',
  shortName: 'Prolonged Power',

}, {
  value: 'old_war',
  name: 'Legion - Potion of the Old War',
  shortName: 'Old War',
}, {
  value: 'deadly_grace',
  name: 'Legion - Potion of Deadly Grace',
  shortName: 'Deadly Grace',
}, {
  value: 'unbending_potion',
  name: 'Legion - Unbending Potion',
  shortName: 'Unbending Potion',
*/
}, {
  value: 'disabled',
  name: 'No Potion',
  shortName: 'No Potion',
}];

export const flasks = [{
  value: '',
  name: 'SimC Default',
  default: true,

}, {

  // Shadowlands flasks

  value: 'spectral_flask_of_power',
  name: 'Spectral Flask of Power',
  shortName: 'Spectral Power',
  itemId: 171276,
}, {
  value: 'spectral_flask_of_stamina',
  name: 'Spectral Flask of Stamina',
  shortName: 'Spectral Stamina',
  itemId: 171278,
}, {

  // 8.2 flasks

  value: 'greater_flask_of_the_currents',
  name: 'BfA - Greater Flask of the Currents (Agility)',
  shortName: 'Greater Currents',
  itemId: 168651,
}, {
  value: 'greater_flask_of_the_undertow',
  name: 'BfA - Greater Flask of the Undertow (Strength)',
  shortName: 'Greater Undertow',
  itemId: 168654,
}, {
  value: 'greater_flask_of_endless_fathoms',
  name: 'BfA - Greater Flask of Endless Fathoms (Intellect)',
  shortName: 'Greater Endless Fathoms',
  itemId: 168652,
}, {
  value: 'greater_flask_of_the_vast_horizon',
  name: 'BfA - Greater Flask of the Vast Horizon (Stamina)',
  shortName: 'Greater Vast Horizon',
  itemId: 168653,

  /*
}, {

  // 8.0 flasks

  value: 'flask_of_the_currents',
  name: 'BfA - Flask of the Currents (Agility)',
  shortName: 'Currents',
  itemId: 152638,
}, {
  value: 'flask_of_the_undertow',
  name: 'BfA - Flask of the Undertow (Strength)',
  shortName: 'Undertow',
  itemId: 152641,
}, {
  value: 'flask_of_endless_fathoms',
  name: 'BfA - Flask of Endless Fathoms (Intellect)',
  shortName: 'Endless Fathoms',
  itemId: 152639,
}, {
  value: 'flask_of_the_vast_horizon',
  name: 'BfA - Flask of the Vast Horizon (Stamina)',
  shortName: 'Vast Horizon',
  itemId: 152640,

}, {

  // Legion

  value: 'flask_of_the_seventh_demon',
  name: 'Legion - Flask of the Seventh Demon (Agility)',
  shortName: 'Seventh Demon',
}, {
  value: 'flask_of_the_countless_armies',
  name: 'Legion - Flask of the Countless Armies (Strength)',
  shortName: 'Countless Armies',
}, {
  value: 'flask_of_the_whispered_pact',
  name: 'Legion - Flask of the Whispered Pact (Intellect)',
  shortName: 'Whispered Pact',
}, {
  value: 'flask_of_ten_thousand_scars',
  name: 'Legion - Flask of Ten Thousand Scars (Stamina)',
  shortName: 'Ten Thousand Scars',
  */
}, {
  value: 'disabled',
  name: 'No Flask',
  shortName: 'No Flask',
}];

export const augmentations = [{
  value: '',
  name: 'SimC Default',
  default: true,
}, {
  value: 'disabled',
  name: 'Disabled',
  shortName: 'No Rune',
}];

export const augmentationItems = [{
  value: 'battlescarred_augmentation',
  name: 'Battle-Scarred Augment Rune',
  itemId: 160053,
}, {
  value: 'veiled_augmentation',
  name: 'Veiled Augmentation',
  itemId: 181468,
}];

export const expansionOptions = expansionOpts;
